import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { filterByNamespace } from '../utils/web3-utils'
import { useEnvironments } from '../hooks/useEnvironments'
import EnvironmentCard from '../components/EnvironmentCard'
import Network from '../components/general/Network'
import useEnvironmentStats from '../hooks/useEnvironmentStats'
import { parseName } from '../utils/math-utils'
import TimelocksList from '../components/TimelocksList'
import { useBookmarks } from '../context/BookmarksContext'

interface EnvironmentsListProps {
  searchTerm?: string
}

interface Environment {
  namespace: string
  chainIds: number[]
  mimicId: string
}

interface ListItemProps {
  item: Environment
}

const ListItem: React.FC<ListItemProps> = ({ item }) => {
  const navigate = useNavigate()
  const { data: stats, isLoading } = useEnvironmentStats(item.mimicId)

  const handleCopyMimicId = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(item.mimicId)
    toast.success('Copied!')
  }

  return (
    <ListRow onClick={() => navigate(`/dashboard/environments/${item.mimicId}/`)}>
      <ListCell>
        <CellName>{parseName(item.namespace)}</CellName>
        <CellMeta>
          <span>{item.mimicId.substring(0, 10)}...{item.mimicId.substring(item.mimicId.length - 8)}</span>
          <CopyBtn onClick={handleCopyMimicId}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </CopyBtn>
        </CellMeta>
      </ListCell>

      <ListCell>
        <NetworkRow>
          {item.chainIds.map((chainId, index) => (
            <Network key={`${chainId}-${index}`} network={chainId} small={true} />
          ))}
        </NetworkRow>
      </ListCell>

      <ListCell className="txs">
        {isLoading ? <SmallSkeleton /> : <>{stats?.transactions24h || 0} txs</>}
      </ListCell>

      <ListCell className="actions">
        <RowActions>
          <RowBtn
            primary
            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/environments/config/${item.mimicId}/`) }}
          >
            Change Onchain
          </RowBtn>
          <RowBtn
            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/environments/${item.mimicId}/`) }}
          >
            Config
          </RowBtn>
        </RowActions>
      </ListCell>
    </ListRow>
  )
}

const EnvironmentsList: React.FC<EnvironmentsListProps> = ({ searchTerm: externalSearchTerm = '' }) => {
  const [activeOnly, setActiveOnly] = useState(true)
  const { data } = useEnvironments(activeOnly)
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards')
  const { bookmarks, isBookmarked, showOnlyBookmarked, setShowOnlyBookmarked } = useBookmarks()

  const effectiveSearchTerm = externalSearchTerm || searchTerm

  const applyFilters = (items: Environment[]) => {
    const searched = filterByNamespace(items, effectiveSearchTerm) ?? []
    if (showOnlyBookmarked) return searched.filter((e) => isBookmarked(e.mimicId))
    return searched
  }

  return (
    <Page>
      {data ? (
        <>
          <TimelocksList />
          <TopBar>
            <SearchWrap>
              <SearchIcon>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search environments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <ClearBtn onClick={() => setSearchTerm('')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </ClearBtn>
              )}
            </SearchWrap>

            <RightControls>
              <BookmarkFilter>
                <input
                  type="checkbox"
                  id="show-all"
                  checked={!activeOnly}
                  onChange={(e) => setActiveOnly(!e.target.checked)}
                />
                <label htmlFor="show-all">Show all</label>
              </BookmarkFilter>
              {bookmarks.length > 0 && (
                <BookmarkFilter>
                  <input
                    type="checkbox"
                    id="show-bookmarked"
                    checked={showOnlyBookmarked}
                    onChange={(e) => setShowOnlyBookmarked(e.target.checked)}
                  />
                  <label htmlFor="show-bookmarked">Bookmarked</label>
                </BookmarkFilter>
              )}
              <ViewToggle>
                <ViewBtn active={viewMode === 'cards'} onClick={() => setViewMode('cards')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  Cards
                </ViewBtn>
                <ViewBtn active={viewMode === 'list'} onClick={() => setViewMode('list')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"></line>
                    <line x1="8" y1="12" x2="21" y2="12"></line>
                    <line x1="8" y1="18" x2="21" y2="18"></line>
                    <line x1="3" y1="6" x2="3.01" y2="6"></line>
                    <line x1="3" y1="12" x2="3.01" y2="12"></line>
                    <line x1="3" y1="18" x2="3.01" y2="18"></line>
                  </svg>
                  List
                </ViewBtn>
              </ViewToggle>
            </RightControls>
          </TopBar>

          {viewMode === 'cards' ? (
            <CardsGrid>
              {applyFilters(data).map((item, index) => (
                <EnvironmentCard
                  key={index}
                  namespace={item.namespace}
                  mimicId={item.mimicId}
                  chainIds={item.chainIds}
                />
              ))}
            </CardsGrid>
          ) : (
            <>
              <ListHeader>
                <span>Environment</span>
                <span>Networks</span>
                <span>Activity</span>
                <span></span>
              </ListHeader>
              <ListBody>
                {applyFilters(data).map((item, index) => (
                  <ListItem key={index} item={item} />
                ))}
              </ListBody>
            </>
          )}
        </>
      ) : (
        <LoaderWrap>
          <LoaderBar />
        </LoaderWrap>
      )}
    </Page>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif;
`

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`

const SearchWrap = styled.div`
  position: relative;
  max-width: 360px;
  flex: 1;
`

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a1a1a1;
  display: flex;
  pointer-events: none;
`

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0 32px 0 32px;
  height: 32px;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #ededed;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: #777; }
  &:focus { border-color: rgba(111, 92, 230, 0.4); }
`

const ClearBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a1a1a1;
  cursor: pointer;
  display: flex;
  padding: 2px;
  &:hover { color: #888; }
`

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`

const BookmarkFilter = styled.div`
  display: flex; align-items: center; gap: 6px; white-space: nowrap;
  font-size: 12px; color: #777;
  input[type='checkbox'] { accent-color: #6f5ce6; cursor: pointer; }
  label { cursor: pointer; user-select: none; }
  &:has(input:checked) label { color: #a89aff; }
`

const ViewToggle = styled.div`
  display: flex;
  gap: 2px;
  background: #111;
  padding: 3px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.07);
`

const ViewBtn = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: ${({ active }) => active ? 'rgba(111,92,230,0.15)' : 'transparent'};
  color: ${({ active }) => active ? '#a89aff' : '#555'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;

  svg { stroke: currentColor; }
  &:hover { color: ${({ active }) => active ? '#a89aff' : '#888'}; }
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  width: 100%;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr 120px 240px;
  gap: 16px;
  padding: 0 16px 8px;
  color: #777;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  margin-bottom: 4px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr auto;
    span:nth-child(2), span:nth-child(3) { display: none; }
  }
`

const ListBody = styled.div`
  display: flex;
  flex-direction: column;
`

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr 120px 240px;
  gap: 16px;
  align-items: center;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;

  &:hover { background: rgba(255,255,255,0.03); }

  @media (max-width: 700px) {
    grid-template-columns: 1fr auto;
    & > *:nth-child(2), & > *:nth-child(3) { display: none; }
  }
`

const ListCell = styled.div`
  display: flex;
  align-items: center;
  color: #ededed;
  font-size: 13px;

  &.txs { color: #a0a0a0; font-variant-numeric: tabular-nums; }
  &.actions { justify-content: flex-end; }
`

const CellName = styled.div`
  color: #ededed;
  font-weight: 500;
  font-size: 14px;
`

const CellMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  color: #a1a1a1;
  font-size: 11px;
  font-family: 'SF Mono', 'Fira Code', monospace;
`

const CopyBtn = styled.div`
  cursor: pointer;
  color: #777;
  display: flex;
  &:hover { color: #777; }
`

const NetworkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  img { margin-right: 0 !important; }
`

const RowActions = styled.div`
  display: flex;
  gap: 6px;
`

const RowBtn = styled.button<{ primary?: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;

  ${({ primary }) => primary ? `
    background: #6f5ce6;
    color: #fff;
    border: none;
    &:hover { background: #5a4bd4; }
  ` : `
    background: transparent;
    color: #a1a1a1;
    border: 1px solid rgba(255,255,255,0.08);
    &:hover { color: #a0a0a0; border-color: rgba(255,255,255,0.15); }
  `}
`

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`

const SmallSkeleton = styled.div`
  width: 48px;
  height: 16px;
  background: linear-gradient(90deg, #1a1a1a 25%, #222 50%, #1a1a1a 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 3px;
`

const loadingAnim = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
`

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 40px;
`

const LoaderBar = styled.div`
  width: 200px;
  height: 1px;
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 25%;
    height: 100%;
    background: linear-gradient(90deg, transparent, #6f5ce6, transparent);
    animation: ${loadingAnim} 1.2s ease-in-out infinite;
  }
`

export default EnvironmentsList
